import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { paymentInstallmentService, PaymentGroupResponse, Installment, Payment } from '../services/paymentInstallmentService';
import customersService, { Customer } from '../services/clientsService';
import { PaymentInstallment } from '../types';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Check,
  TrendingUp,
  CreditCard,
  Banknote,
  CalendarDays,
  User,
  Building2,
  Hash,
  Loader2,
  AlertCircle,
  Wallet,
  RefreshCw,
  ChevronDown,
  ShoppingBag,
  Receipt,
  ChevronRight,
} from 'lucide-react';
import MarkAsPaidModal, { PaymentConfirmationData } from '../components/Sales/MarkAsPaidModal';
import RequirePermission from '../components/RequirePermission';
import { PERMISSIONS } from '../constants/permissions';

export default function CustomerPaymentDetail() {
  const { customerId, saleId } = useParams<{ customerId: string; saleId: string }>();
  const navigate = useNavigate();
  const [groupedPayments, setGroupedPayments] = useState<PaymentGroupResponse[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPayments, setExpandedPayments] = useState<Set<number>>(new Set());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<PaymentInstallment | null>(null);

  const isPastDue = useCallback((dueDate: string, isPaid: boolean) => {
    if (isPaid) return false;
    return new Date(dueDate) < new Date();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const customerIdNum = parseInt(customerId!);
      const [customerData, response] = await Promise.all([
        customersService.getCustomerById(customerIdNum),
        paymentInstallmentService.getCustomerInstallments(customerIdNum),
      ]);
      setCustomer(customerData);
      setGroupedPayments(response);
      setError(null);
    } catch (err) {
      setError('Error al cargar la información');
      console.error('Error fetching customer payments:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) fetchData();
  }, [customerId, fetchData]);

  // Filter payments for this saleId only
  const salePayments = useMemo(() => {
    const sid = parseInt(saleId!);
    return groupedPayments.filter(pg => pg.sale?.saleId === sid);
  }, [groupedPayments, saleId]);

  const saleInfo = salePayments[0]?.sale ?? null;

  // Auto-expand groups with overdue installments on load
  useEffect(() => {
    if (salePayments.length === 0) return;
    const initial = new Set<number>();
    const hasOverdue = salePayments.some(pg =>
      pg.installments.some(i => isPastDue(i.dueDate, i.isPaid))
    );
    if (hasOverdue) {
      salePayments.forEach(pg => {
        if (pg.installments.some(i => isPastDue(i.dueDate, i.isPaid))) {
          initial.add(pg.payment.paymentId);
        }
      });
    } else {
      // Expand first payment by default
      if (salePayments[0]) initial.add(salePayments[0].payment.paymentId);
    }
    setExpandedPayments(initial);
  }, [salePayments, isPastDue]);

  const toggleExpand = (paymentId: number) => {
    setExpandedPayments(prev => {
      const next = new Set(prev);
      next.has(paymentId) ? next.delete(paymentId) : next.add(paymentId);
      return next;
    });
  };

  const getCustomerName = (c: Customer) =>
    c.type === 'PERSON' ? `${c.firstName} ${c.lastName}` : c.businessName;

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency.toUpperCase() === 'USD' ? 'US$' : '$';
    return `${symbol} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getPaymentTypeLabel = (type: string) =>
    ({ EFECTIVO: 'Efectivo', TRANSFERENCIA: 'Transferencia', CHEQUE: 'Cheque', FINANCIADO: 'Financiado', OTRO: 'Otro' }[type] || type);

  const getFinancingTypeLabel = (type: string) => ({
    CUOTAS_MENSUALES: 'Mensuales (30 días)',
    CUOTAS_BIMENSUALES: 'Bimensuales (60 días)',
    CUOTAS_TRIMESTRALES: 'Trimestrales (90 días)',
    CUOTAS_CUATRIMESTRALES: 'Cuatrimestrales (120 días)',
    CUOTAS_PENTAMESTRALES: 'Pentamestrales (150 días)',
    CUOTAS_SEMESTRALES: 'Semestrales (180 días)',
    CUOTAS_ANUALES: 'Anuales (365 días)',
    LIBRE: 'Libre',
  }[type] || type);

  const getIndexationLabel = (type: string) => ({
    CACG: 'CAC General',
    CACO: 'CAC Obra',
    CACM: 'CAC Materiales',
  }[type] || type);

  const getMoraFrequencyLabel = (type: string) => ({
    DAILY: 'Diaria',
    MONTHLY: 'Mensual',
    ANNUAL: 'Anual',
  }[type] || type);

  const getAccountingTypeLabel = (type: string) => ({
    A: 'Tipo A (Resp. Inscripto)',
    B: 'Tipo B (Cons. Final)',
    C: 'Tipo C (Monotributista)',
  }[type] || type);

  const handleMarkAsPaid = (installment: Installment, payment: Payment) => {
    const pi: PaymentInstallment = {
      installmentId: installment.installmentId,
      installmentAmount: installment.amount,
      adjustedAmount: installment.adjustedAmount,
      dueDate: installment.dueDate,
      isPaid: installment.isPaid,
      paymentDate: installment.paymentDate,
      accountingDate: installment.accountingDate,
      cashRegister: installment.cashRegister,
      costCenter: installment.costCenter,
      perceptions: installment.perceptions,
      comments: installment.comments,
      installmentCreatedAt: installment.createdAt,
      installmentUpdatedAt: installment.updatedAt,
      paymentId: payment.paymentId,
      currency: payment.currency,
      paymentType: payment.paymentType,
      accountingType: payment.accountingType,
      description: payment.description || '',
      totalAmount: payment.totalAmount,
      exchangeRate: payment.exchangeRate,
      generalPaymentDate: payment.generalPaymentDate || '',
      iva: payment.iva,
      financingType: payment.financingType,
      firstInstallmentDate: payment.firstInstallmentDate,
      totalInstallments: payment.totalInstallments,
      indexationType: payment.indexationType,
      indexBaseDate: payment.indexBaseDate,
      moraRate: payment.moraRate,
      moraFrequency: payment.moraFrequency,
      currentMoraPreview: installment.currentMoraPreview,
      saleId: parseInt(saleId!),
      deliveryDate: saleInfo?.deliveryDate || '',
      saleCurrency: saleInfo?.currency || payment.currency,
      saleTotal: saleInfo?.total || 0,
    };
    setSelectedInstallment(pi);
    setIsModalOpen(true);
  };

  const handleConfirmPayment = async (paymentData: PaymentConfirmationData) => {
    try {
      const req: any = {
        payment_installment_id: paymentData.installmentId,
        payment_date: paymentData.paymentDate,
        accounting_date: paymentData.accountingDate,
        payment_source: paymentData.paymentSource,
        perceptions: paymentData.perceptions,
        comments: paymentData.comments,
      };
      if (paymentData.paymentSource === 'CASHBOX') {
        req.cash_box_id = paymentData.cashBoxId;
        req.payment_methods = paymentData.paymentMethods?.map(pm => ({ method: pm.method, amount: pm.amount })) || [];
      } else if (paymentData.paymentSource === 'CURRENT_ACCOUNT') {
        req.customer_current_account_id = paymentData.customerCurrentAccountId;
      }
      await paymentInstallmentService.markAsPaid(req);
      await fetchData();
    } catch (err) {
      console.error('Error al marcar cuota como pagada:', err);
      throw err;
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Cargando detalle de cobranzas...</p>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error || !customer) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-destructive/10">
            <AlertCircle className="w-6 h-6 text-destructive" />
          </div>
          <p className="text-sm font-medium text-destructive">{error || 'No se encontró información'}</p>
          <Button onClick={() => navigate(`/customer-payments/${customerId}`)} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Ventas
          </Button>
        </div>
      </div>
    );
  }

  const isPerson = customer.type === 'PERSON';
  const allInstallments = salePayments.flatMap(p => p.installments);
  const paidCount = allInstallments.filter(i => i.isPaid).length;
  const totalCount = allInstallments.length;
  const overdueCount = allInstallments.filter(i => isPastDue(i.dueDate, i.isPaid)).length;
  const paidAmount = allInstallments.filter(i => i.isPaid).reduce((s, i) => s + i.adjustedAmount, 0);
  const totalAmount = allInstallments.reduce((s, i) => s + i.adjustedAmount, 0);
  const progressPct = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
  const mainCurrency = salePayments[0]?.payment.currency || 'ARS';

  return (
    <div className="w-full h-full p-4 sm:p-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

        {/* ── Header ── */}
        <div className="px-4 py-5 border-b sm:px-6 border-border bg-gradient-to-r from-card to-muted/20">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            {/* Breadcrumb navigation */}
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={() => navigate(`/customer-payments/${customerId}`)}
                className="flex items-center gap-2 rounded-lg shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                Ventas
              </Button>

              {/* Breadcrumb trail */}
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <button
                  onClick={() => navigate('/customer-payments')}
                  className="hover:text-foreground transition-colors"
                >
                  Cobranzas
                </button>
                <ChevronRight className="w-3.5 h-3.5" />
                <button
                  onClick={() => navigate(`/customer-payments/${customerId}`)}
                  className="hover:text-foreground transition-colors max-w-[140px] truncate"
                >
                  {getCustomerName(customer)}
                </button>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-foreground font-medium">Venta #{saleId}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="shrink-0">
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>

          {/* Sale info row */}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <ShoppingBag className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Venta #{saleId}</h1>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <div className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md ${
                    isPerson ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isPerson ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                    {getCustomerName(customer)}
                  </div>
                  {saleInfo?.deliveryDate && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      Entrega: {formatDate(saleInfo.deliveryDate)}
                    </div>
                  )}
                  {saleInfo && (
                    <div className="flex items-center gap-1 text-xs font-semibold text-foreground">
                      <Wallet className="w-3 h-3" />
                      {formatCurrency(saleInfo.total, saleInfo.currency)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-5 sm:p-6">

          {/* ── Banner de mora ── */}
          {overdueCount > 0 && (
            <div className="flex items-center gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/5">
              <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-destructive">
                  {overdueCount} cuota{overdueCount !== 1 ? 's' : ''} vencida{overdueCount !== 1 ? 's' : ''} en esta venta
                </p>
                <p className="text-xs text-destructive/70 mt-0.5">Revisá los tipos de pago con mora a continuación</p>
              </div>
            </div>
          )}

          {/* ── Resumen de la venta ── */}
          <div className="bg-background border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-muted-foreground">
                  Progreso · <span className="text-foreground font-semibold tabular-nums">{paidCount}/{totalCount}</span> cuotas cobradas
                </p>
                <div className="flex items-center gap-3 sm:min-w-[200px]">
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        progressPct === 100 ? 'bg-success' : overdueCount > 0 ? 'bg-destructive' : 'bg-primary'
                      }`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-foreground whitespace-nowrap">
                    {Math.round(progressPct)}%
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 divide-x divide-border">
              {[
                { label: 'Total cuotas', value: formatCurrency(totalAmount, mainCurrency), icon: Wallet, color: 'text-foreground' },
                { label: 'Cobrado', value: formatCurrency(paidAmount, mainCurrency), icon: CheckCircle, color: 'text-success' },
                { label: 'Pendiente', value: formatCurrency(totalAmount - paidAmount, mainCurrency), icon: Clock, color: overdueCount > 0 ? 'text-destructive' : 'text-info' },
              ].map((s) => (
                <div key={s.label} className="p-4 flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </div>
                  <p className={`text-base font-bold tabular-nums ${s.color}`}>{s.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Tarjetas de tipos de pago ── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-base font-semibold text-foreground">Tipos de pago</h2>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                {salePayments.length} {salePayments.length === 1 ? 'tipo' : 'tipos'}
              </span>
            </div>

            {salePayments.length === 0 ? (
              <div className="bg-card border border-border rounded-xl flex flex-col items-center justify-center py-16 text-center">
                <div className="flex items-center justify-center w-14 h-14 mb-4 rounded-2xl bg-muted/50">
                  <Receipt className="w-7 h-7 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-medium text-muted-foreground">Sin tipos de pago en esta venta</p>
              </div>
            ) : (
              <div className="space-y-3">
                {salePayments.map((pg) => {
                  const isFinancing = pg.payment.paymentType === 'FINANCIADO' || (pg.payment.totalInstallments ?? 0) > 1;
                  const pgPaid = pg.installments.filter(i => i.isPaid).length;
                  const pgTotal = pg.installments.length;
                  const pgProgress = pgTotal > 0 ? (pgPaid / pgTotal) * 100 : 0;
                  const pgOverdue = pg.installments.some(i => isPastDue(i.dueDate, i.isPaid));
                  const pgAllPaid = pgPaid === pgTotal && pgTotal > 0;
                  const isExpanded = expandedPayments.has(pg.payment.paymentId);
                  const paidAmt = pg.installments.filter(i => i.isPaid).reduce((s, i) => s + i.adjustedAmount, 0);
                  const pendingAmt = pg.installments.filter(i => !i.isPaid).reduce((s, i) => s + i.adjustedAmount, 0);

                  const hasAccountingDate = pg.installments.some(i => i.accountingDate);
                  const hasCashRegister = pg.installments.some(i => i.cashRegister);
                  const hasPerceptions = pg.installments.some(i => i.perceptions != null && Number(i.perceptions) > 0);
                  const hasComments = pg.installments.some(i => i.comments);

                  return (
                    <div
                      key={pg.payment.paymentId}
                      className={`bg-card border rounded-xl overflow-hidden transition-all duration-200 ${
                        pgOverdue
                          ? 'border-destructive/40'
                          : pgAllPaid
                          ? 'border-success/30'
                          : 'border-border'
                      }`}
                    >
                      {/* ── Cabecera del tipo de pago ── */}
                      <button
                        onClick={() => toggleExpand(pg.payment.paymentId)}
                        className="w-full text-left hover:bg-muted/20 transition-colors"
                      >
                        <div className={`p-5 ${pgOverdue ? 'bg-destructive/5' : pgAllPaid ? 'bg-success/5' : 'bg-muted/10'}`}>
                          <div className="flex items-start justify-between gap-4">
                            {/* Left: Type icon + info */}
                            <div className="flex items-start gap-4 min-w-0 flex-1">
                              <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                                pgOverdue ? 'bg-destructive/10' :
                                pgAllPaid ? 'bg-success/10' :
                                isFinancing ? 'bg-info/10' : 'bg-success/10'
                              }`}>
                                {isFinancing
                                  ? <CalendarDays className={`w-5 h-5 ${pgOverdue ? 'text-destructive' : pgAllPaid ? 'text-success' : 'text-info'}`} />
                                  : <Banknote className={`w-5 h-5 ${pgOverdue ? 'text-destructive' : pgAllPaid ? 'text-success' : 'text-success'}`} />
                                }
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <p className="text-base font-bold text-foreground">
                                    {pg.payment.description || getPaymentTypeLabel(pg.payment.paymentType)}
                                  </p>
                                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                    pgOverdue ? 'bg-destructive/10 text-destructive' :
                                    pgAllPaid ? 'bg-success/10 text-success' :
                                    isFinancing ? 'bg-info/10 text-info' : 'bg-success/10 text-success'
                                  }`}>
                                    {pgOverdue && <XCircle className="w-3 h-3" />}
                                    {pgAllPaid && <CheckCircle className="w-3 h-3" />}
                                    {!pgOverdue && !pgAllPaid && isFinancing && <Clock className="w-3 h-3" />}
                                    {!pgOverdue && !pgAllPaid && !isFinancing && <Clock className="w-3 h-3" />}
                                    {pgOverdue ? 'Con mora' : pgAllPaid ? 'Cobrado' : 'Pendiente'}
                                  </span>
                                  <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border border-border text-muted-foreground">
                                    {pg.payment.currency}
                                  </span>
                                </div>

                                {/* Config pills */}
                                <div className="flex flex-wrap gap-x-3 gap-y-1">
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" />
                                    {getPaymentTypeLabel(pg.payment.paymentType)}
                                  </span>
                                  {pg.payment.accountingType && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Receipt className="w-3 h-3" />
                                      {getAccountingTypeLabel(pg.payment.accountingType)}
                                    </span>
                                  )}
                                  {pg.payment.financingType && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <CalendarDays className="w-3 h-3" />
                                      {getFinancingTypeLabel(pg.payment.financingType)}
                                    </span>
                                  )}
                                  {pg.payment.totalInstallments && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Hash className="w-3 h-3" />
                                      {pg.payment.totalInstallments} cuotas
                                    </span>
                                  )}
                                  {pg.payment.indexationType && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <TrendingUp className="w-3 h-3" />
                                      {getIndexationLabel(pg.payment.indexationType)}
                                    </span>
                                  )}
                                  {pg.payment.moraRate && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      Mora {pg.payment.moraRate}% {getMoraFrequencyLabel(pg.payment.moraFrequency || '')}
                                    </span>
                                  )}
                                  {pg.payment.firstInstallmentDate && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      1ra cuota: {formatDate(pg.payment.firstInstallmentDate)}
                                    </span>
                                  )}
                                  {pg.payment.generalPaymentDate && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      Fecha: {formatDate(pg.payment.generalPaymentDate)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Right: amounts + progress + chevron */}
                            <div className="flex items-center gap-3 shrink-0">
                              <div className="text-right">
                                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Total</p>
                                <p className="text-lg font-bold text-foreground tabular-nums">
                                  {formatCurrency(pg.payment.totalAmount, pg.payment.currency)}
                                </p>
                                {isFinancing && pgTotal > 1 && (
                                  <div className="mt-1.5 flex flex-col items-end gap-1">
                                    <div className="flex items-center gap-1.5">
                                      <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full ${pgProgress === 100 ? 'bg-success' : pgOverdue ? 'bg-destructive' : 'bg-primary'}`}
                                          style={{ width: `${pgProgress}%` }}
                                        />
                                      </div>
                                      <span className="text-[10px] font-medium text-muted-foreground tabular-nums">{pgPaid}/{pgTotal}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>

                          {/* Mini stat row */}
                          {isFinancing && (
                            <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border/60">
                              {[
                                { label: 'Cobrado', value: formatCurrency(paidAmt, pg.payment.currency), color: 'text-success' },
                                { label: 'Pendiente', value: formatCurrency(pendingAmt, pg.payment.currency), color: pgOverdue ? 'text-destructive' : 'text-info' },
                                { label: 'Cuotas', value: `${pgPaid}/${pgTotal}`, color: 'text-foreground' },
                              ].map((stat) => (
                                <div key={stat.label} className="text-center">
                                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{stat.label}</p>
                                  <p className={`text-sm font-bold tabular-nums mt-0.5 ${stat.color}`}>{stat.value}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </button>

                      {/* ── Panel expandido: tabla de cuotas ── */}
                      {isExpanded && (
                        <>
                          {/* Condiciones adicionales si hay indexación/mora */}
                          {(pg.payment.iva && Number(pg.payment.iva) > 0 || pg.payment.exchangeRate && pg.payment.currency !== 'ARS') && (
                            <div className="px-5 py-3 border-t border-border bg-muted/5">
                              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Detalles adicionales</p>
                              <div className="flex flex-wrap gap-x-6 gap-y-2">
                                {pg.payment.iva && Number(pg.payment.iva) > 0 && (
                                  <div>
                                    <p className="text-[10px] text-muted-foreground">Monto base</p>
                                    <p className="text-sm font-semibold text-foreground">{formatCurrency(pg.payment.baseAmount, pg.payment.currency)}</p>
                                  </div>
                                )}
                                {pg.payment.iva && Number(pg.payment.iva) > 0 && (
                                  <div>
                                    <p className="text-[10px] text-muted-foreground">IVA</p>
                                    <p className="text-sm font-semibold text-foreground">{pg.payment.iva}%</p>
                                  </div>
                                )}
                                {pg.payment.exchangeRate && pg.payment.currency !== 'ARS' && (
                                  <div>
                                    <p className="text-[10px] text-muted-foreground">Tipo de cambio</p>
                                    <p className="text-sm font-semibold text-foreground">
                                      $ {Number(pg.payment.exchangeRate).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                                    </p>
                                  </div>
                                )}
                                {pg.payment.indexBaseDate && (
                                  <div>
                                    <p className="text-[10px] text-muted-foreground">Fecha base índice</p>
                                    <p className="text-sm font-semibold text-foreground">{formatDate(pg.payment.indexBaseDate)}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* ── Cuotas ── */}
                          <div className="border-t border-border">
                            <div className="px-5 py-3 bg-muted/30 border-b border-border">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                {isFinancing ? `Cuotas (${pgTotal})` : 'Pago único'}
                              </p>
                            </div>

                            {/* Mobile cards for installments */}
                            <div className="sm:hidden divide-y divide-border">
                              {pg.installments.map((installment, idx) => {
                                const isOverdue = isPastDue(installment.dueDate, installment.isPaid);
                                const totalInst = pg.payment.totalInstallments || pg.installments.length;
                                return (
                                  <div
                                    key={installment.installmentId}
                                    className={`p-4 ${isOverdue ? 'bg-destructive/5' : installment.isPaid ? 'bg-success/5' : ''}`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0 flex-1">
                                        {isFinancing && (
                                          <p className="text-xs font-semibold text-muted-foreground mb-1">
                                            Cuota {idx + 1}/{totalInst}
                                          </p>
                                        )}
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {installment.isPaid ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-success/10 text-success">
                                              <CheckCircle className="w-3 h-3" />
                                              Cobrado
                                            </span>
                                          ) : isOverdue ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
                                              <XCircle className="w-3 h-3" />
                                              Vencido
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-info/10 text-info">
                                              <Clock className="w-3 h-3" />
                                              Pendiente
                                            </span>
                                          )}
                                        </div>
                                        <div className="mt-2 space-y-1">
                                          <div className="flex items-center gap-1.5">
                                            <Calendar className="w-3 h-3 text-muted-foreground" />
                                            <span className={`text-sm ${isOverdue ? 'text-destructive font-medium' : 'text-foreground'}`}>
                                              Vence: {formatDate(installment.dueDate)}
                                            </span>
                                          </div>
                                          <p className="text-base font-bold text-foreground tabular-nums">
                                            {formatCurrency(installment.adjustedAmount, pg.payment.currency)}
                                          </p>
                                          {installment.adjustedAmount !== installment.amount && (
                                            <p className="text-xs text-muted-foreground line-through tabular-nums">
                                              {formatCurrency(installment.amount, pg.payment.currency)}
                                            </p>
                                          )}
                                          {installment.isPaid && installment.paymentDate && (
                                            <p className="text-xs text-muted-foreground">
                                              Cobrado: {formatDate(installment.paymentDate)}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      {!installment.isPaid && (
                                        <RequirePermission permission={PERMISSIONS.CONTABILIDAD.COBRANZAS_OPERAR}>
                                          <Button
                                            size="sm"
                                            className="gap-1.5 text-xs h-9 bg-success text-white hover:bg-success/90 shrink-0"
                                            onClick={() => handleMarkAsPaid(installment, pg.payment)}
                                          >
                                            <Check className="w-3.5 h-3.5" />
                                            Cobrar
                                          </Button>
                                        </RequirePermission>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Desktop table */}
                            <div className="hidden sm:block overflow-x-auto">
                              <table className="w-full min-w-[560px]">
                                <thead>
                                  <tr className="border-b border-border bg-muted/20">
                                    {isFinancing && (
                                      <th className="px-5 py-3 text-left">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Cuota</span>
                                      </th>
                                    )}
                                    <th className="px-5 py-3 text-left">
                                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vencimiento</span>
                                    </th>
                                    <th className="px-5 py-3 text-left">
                                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monto</span>
                                    </th>
                                    {pg.payment.indexationType && (
                                      <th className="px-5 py-3 text-left">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ajustado</span>
                                      </th>
                                    )}
                                    {pg.payment.moraRate && (
                                      <th className="px-5 py-3 text-left">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Mora</span>
                                      </th>
                                    )}
                                    <th className="px-5 py-3 text-left">
                                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Estado</span>
                                    </th>
                                    <th className="px-5 py-3 text-left">
                                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">F. Cobro</span>
                                    </th>
                                    {hasAccountingDate && (
                                      <th className="px-5 py-3 text-left">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">F. Contable</span>
                                      </th>
                                    )}
                                    {hasCashRegister && (
                                      <th className="px-5 py-3 text-left">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Caja</span>
                                      </th>
                                    )}
                                    {hasPerceptions && (
                                      <th className="px-5 py-3 text-right">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Percepciones</span>
                                      </th>
                                    )}
                                    {hasComments && (
                                      <th className="px-5 py-3 text-left">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notas</span>
                                      </th>
                                    )}
                                    <th className="px-5 py-3 text-center">
                                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Acción</span>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pg.installments.map((installment, idx) => {
                                    const isOverdue = isPastDue(installment.dueDate, installment.isPaid);
                                    const isLast = idx === pg.installments.length - 1;
                                    const cell = isLast ? 'px-5 py-4' : 'px-5 py-4 border-b border-border';
                                    const totalInst = pg.payment.totalInstallments || pg.installments.length;

                                    return (
                                      <tr
                                        key={installment.installmentId}
                                        className={`transition-colors ${
                                          isOverdue
                                            ? 'bg-destructive/5 hover:bg-destructive/10'
                                            : installment.isPaid
                                            ? 'bg-success/5 hover:bg-success/10'
                                            : 'hover:bg-muted/30'
                                        }`}
                                      >
                                        {isFinancing && (
                                          <td className={cell}>
                                            <div>
                                              <span className="text-sm font-semibold text-foreground tabular-nums">
                                                {idx + 1}<span className="text-muted-foreground font-normal">/{totalInst}</span>
                                              </span>
                                              {installment.createdAt && (
                                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                                  Reg. {formatDate(installment.createdAt.split('T')[0])}
                                                </p>
                                              )}
                                            </div>
                                          </td>
                                        )}
                                        <td className={cell}>
                                          <div>
                                            <span className={`text-sm ${isOverdue ? 'text-destructive font-semibold' : 'text-foreground'}`}>
                                              {formatDate(installment.dueDate)}
                                            </span>
                                            {!isFinancing && installment.createdAt && (
                                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                                Reg. {formatDate(installment.createdAt.split('T')[0])}
                                              </p>
                                            )}
                                          </div>
                                        </td>
                                        <td className={cell}>
                                          <span className={`text-sm tabular-nums ${
                                            installment.adjustedAmount !== installment.amount
                                              ? 'text-muted-foreground line-through'
                                              : 'font-semibold text-foreground'
                                          }`}>
                                            {formatCurrency(installment.amount, pg.payment.currency)}
                                          </span>
                                        </td>
                                        {pg.payment.indexationType && (
                                          <td className={cell}>
                                            {installment.adjustedAmount !== installment.amount ? (
                                              <div>
                                                <p className="text-sm font-bold text-amount tabular-nums">
                                                  {formatCurrency(installment.adjustedAmount, pg.payment.currency)}
                                                </p>
                                                <p className="text-[10px] text-muted-foreground tabular-nums">
                                                  +{formatCurrency(installment.adjustedAmount - installment.amount, pg.payment.currency)}
                                                </p>
                                                {installment.adjustedToMonth && (
                                                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                                                    {installment.isAdjustedEstimate ? '≈ ' : ''}
                                                    {new Date(installment.adjustedToMonth + 'T00:00:00').toLocaleDateString('es-AR', { month: 'short', year: 'numeric' })}
                                                    {installment.isAdjustedEstimate && <span className="italic"> (est.)</span>}
                                                  </p>
                                                )}
                                              </div>
                                            ) : new Date(installment.dueDate) > new Date() ? (
                                              <span className="text-sm font-semibold text-foreground tabular-nums">
                                                {formatCurrency(installment.amount, pg.payment.currency)}
                                              </span>
                                            ) : (
                                              <div>
                                                <span className="text-sm text-muted-foreground/50 tabular-nums">
                                                  {formatCurrency(installment.amount, pg.payment.currency)}
                                                </span>
                                                <p className="text-[10px] text-muted-foreground/50 mt-0.5">sin datos CAC</p>
                                              </div>
                                            )}
                                          </td>
                                        )}
                                        {pg.payment.moraRate && (
                                          <td className={cell}>
                                            {installment.isPaid && installment.moraAmount && installment.moraAmount > 0 ? (
                                              <div title="Mora cobrada al pagar">
                                                <p className="text-sm font-bold tabular-nums text-warning">
                                                  +{formatCurrency(installment.moraAmount, pg.payment.currency)}
                                                </p>
                                                <p className="text-[10px] text-muted-foreground tabular-nums">
                                                  Total: {formatCurrency(installment.moraTotalAmount ?? installment.amount, pg.payment.currency)}
                                                </p>
                                              </div>
                                            ) : !installment.isPaid && installment.currentMoraPreview && installment.currentMoraPreview > 0 ? (
                                              <div title={`Mora estimada (${pg.payment.moraRate}% ${getMoraFrequencyLabel(pg.payment.moraFrequency || '')})`}>
                                                <p className="text-sm font-medium tabular-nums text-destructive">
                                                  +{formatCurrency(installment.currentMoraPreview, pg.payment.currency)}
                                                </p>
                                                <p className="text-[10px] text-muted-foreground">estimado</p>
                                              </div>
                                            ) : (
                                              <span className="text-sm text-muted-foreground">—</span>
                                            )}
                                          </td>
                                        )}
                                        <td className={cell}>
                                          {installment.isPaid ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-success/10 text-success">
                                              <CheckCircle className="w-3 h-3" />
                                              Cobrado
                                            </span>
                                          ) : isOverdue ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-destructive/10 text-destructive">
                                              <XCircle className="w-3 h-3" />
                                              Vencido
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-info/10 text-info">
                                              <Clock className="w-3 h-3" />
                                              Pendiente
                                            </span>
                                          )}
                                        </td>
                                        <td className={cell}>
                                          <span className="text-sm text-muted-foreground">
                                            {installment.paymentDate ? formatDate(installment.paymentDate) : '—'}
                                          </span>
                                        </td>
                                        {hasAccountingDate && (
                                          <td className={cell}>
                                            <span className="text-sm text-muted-foreground">
                                              {installment.accountingDate ? formatDate(installment.accountingDate) : '—'}
                                            </span>
                                          </td>
                                        )}
                                        {hasCashRegister && (
                                          <td className={cell}>
                                            <span className="text-sm text-muted-foreground">
                                              {installment.cashRegister || '—'}
                                            </span>
                                          </td>
                                        )}
                                        {hasPerceptions && (
                                          <td className={`${cell} text-right`}>
                                            <span className="text-sm text-muted-foreground tabular-nums">
                                              {installment.perceptions != null && Number(installment.perceptions) > 0
                                                ? formatCurrency(Number(installment.perceptions), pg.payment.currency)
                                                : '—'}
                                            </span>
                                          </td>
                                        )}
                                        {hasComments && (
                                          <td className={cell}>
                                            <span
                                              className="text-sm text-muted-foreground max-w-[160px] truncate block"
                                              title={installment.comments || ''}
                                            >
                                              {installment.comments || '—'}
                                            </span>
                                          </td>
                                        )}
                                        <td className={`${cell} text-center`}>
                                          {!installment.isPaid ? (
                                            <RequirePermission permission={PERMISSIONS.CONTABILIDAD.COBRANZAS_OPERAR}>
                                              <Button
                                                size="sm"
                                                className="gap-1.5 text-xs h-8 bg-success text-white hover:bg-success/90"
                                                onClick={() => handleMarkAsPaid(installment, pg.payment)}
                                              >
                                                <Check className="w-3.5 h-3.5" />
                                                Cobrar
                                              </Button>
                                            </RequirePermission>
                                          ) : (
                                            <span className="text-xs text-muted-foreground/40">—</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      <MarkAsPaidModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedInstallment(null); }}
        onConfirm={handleConfirmPayment}
        installment={selectedInstallment}
        customer={customer}
      />
    </div>
  );
}
